import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const SEOContent = () => {
  const { language } = useLanguage();
  const [expandedSection, setExpandedSection] = useState(null);

  const content = {
    en: {
      mainTitle: "Ultimate Texas Hold'em - Complete Guide",
      intro: "Ultimate Texas Hold'em is one of the most popular casino poker games in the world. Unlike traditional poker where you play against other players, here you compete against the dealer. Our free online version lets you practice and master the game without risking real money.",
      
      rulesTitle: "📜 Official Rules",
      rules: [
        { title: "Initial Bets", text: "Place equal Ante and Blind bets before receiving cards. The optional Trips bet pays on your hand strength regardless of the dealer's hand." },
        { title: "Hole Cards", text: "You receive 2 private cards (hole cards). The dealer also receives 2 cards face down." },
        { title: "Pre-Flop Decision", text: "After seeing your cards, you can: Bet 3x or 4x your Ante (strong hands), or Check (wait for more cards)." },
        { title: "The Flop", text: "3 community cards are revealed. If you haven't bet yet, you can: Bet 2x your Ante, or Check again." },
        { title: "Turn & River", text: "The final 2 community cards are revealed. If you haven't bet yet, you must: Bet 1x your Ante, or Fold (lose Ante and Blind)." },
        { title: "Showdown", text: "Both hands are revealed. Best 5-card poker hand wins. Dealer must have a pair or better to 'qualify'." },
      ],
      
      payoutsTitle: "💰 Payout Tables",
      tripsPayouts: [
        { hand: "Royal Flush", payout: "50:1" },
        { hand: "Straight Flush", payout: "40:1" },
        { hand: "Four of a Kind", payout: "30:1" },
        { hand: "Full House", payout: "8:1" },
        { hand: "Flush", payout: "6:1" },
        { hand: "Straight", payout: "5:1" },
        { hand: "Three of a Kind", payout: "3:1" },
      ],
      blindPayouts: [
        { hand: "Royal Flush", payout: "500:1" },
        { hand: "Straight Flush", payout: "50:1" },
        { hand: "Four of a Kind", payout: "10:1" },
        { hand: "Full House", payout: "3:1" },
        { hand: "Flush", payout: "3:2" },
        { hand: "Straight", payout: "1:1" },
      ],
      
      strategyTitle: "🎯 Winning Strategy",
      strategies: [
        { title: "Bet 4x Pre-Flop", situations: ["Any pair (especially high pairs)", "Suited Ace with any card", "Ace-King, Ace-Queen, Ace-Jack (suited or not)"] },
        { title: "Bet 3x Pre-Flop", situations: ["Ace with 2-10 (unsuited)", "King-Queen suited", "Any two cards J or higher"] },
        { title: "Check Pre-Flop", situations: ["Weak hands without an Ace or pair", "Small suited connectors", "Wait for the flop to improve"] },
        { title: "Bet 2x on Flop", situations: ["Two pair or better", "Hidden pair (using both hole cards)", "Four to a flush or open-ended straight"] },
        { title: "Bet 1x on River", situations: ["Any made hand (pair or better)", "Better to bet 1x than fold with marginal hands"] },
        { title: "When to Fold", situations: ["Less than 21 outs (dealer beat probability)", "No pair, no draw after river", "Very weak hands with no improvement possible"] },
      ],
      
      faqTitle: "❓ Frequently Asked Questions",
      faqs: [
        { 
          q: "What is Ultimate Texas Hold'em?", 
          a: "Ultimate Texas Hold'em is a casino table game based on Texas Hold'em poker. You play against the dealer, not other players. The goal is to make the best 5-card hand using your 2 hole cards and 5 community cards."
        },
        { 
          q: "What happens if the dealer doesn't qualify?", 
          a: "If the dealer doesn't have at least a pair, your Ante bet is returned (push), but your Play and Blind bets are still paid if you win."
        },
        { 
          q: "What is the house edge?", 
          a: "With optimal strategy, the house edge is approximately 2.19% on the Ante bet. The Trips bet has a higher house edge around 3.5%."
        },
        { 
          q: "Can I play for free?", 
          a: "Yes! Our game uses virtual chips only. It's 100% free with no real money involved - perfect for learning and practicing."
        },
        { 
          q: "What's the difference between Ante and Blind?", 
          a: "Both are mandatory equal bets. The Ante pays 1:1 when you win (or pushes if dealer doesn't qualify). The Blind has bonus payouts for strong hands (Straight or better)."
        },
        { 
          q: "Should I always play the Trips bet?", 
          a: "The Trips bet is optional and independent of the main game. It pays based on your hand strength regardless of whether you beat the dealer. It has a higher house edge but offers big payouts for premium hands."
        },
      ],
      
      tipsTitle: "💡 Pro Tips",
      tips: [
        "Always bet 4x with a pair - the odds heavily favor aggressive play with made hands",
        "Never fold before the river unless you have absolutely nothing",
        "The Blind bet is where big money is made - it pays up to 500:1 for Royal Flush",
        "Use our Advice feature to learn optimal strategy while playing",
        "Manage your bankroll - bet sizes should be consistent relative to your stack",
        "Practice here for free before playing in real casinos",
      ],
      
      historyTitle: "📚 History of the Game",
      history: "Ultimate Texas Hold'em was created by Roger Snow and first introduced in 2006. It quickly became one of the most popular casino table games due to its strategic depth and exciting gameplay. The game combines the familiar mechanics of Texas Hold'em poker with casino-style betting against the house. Today, it's offered in casinos worldwide and has become a staple of online gaming platforms.",
      
      glossaryTitle: "📖 Glossary",
      glossary: [
        { term: "Ante", def: "Mandatory initial bet placed before receiving cards" },
        { term: "Blind", def: "Equal mandatory bet placed alongside Ante, with bonus payouts" },
        { term: "Trips", def: "Optional side bet that pays based on your hand strength" },
        { term: "Play Bet", def: "Additional bet made during the hand (1x to 4x Ante)" },
        { term: "Qualify", def: "Dealer must have pair or better to qualify" },
        { term: "Push", def: "Tie - your bet is returned" },
        { term: "Hole Cards", def: "Your 2 private cards" },
        { term: "Community Cards", def: "5 shared cards (Flop, Turn, River)" },
      ],
    },
    
    fr: {
      mainTitle: "Ultimate Texas Hold'em - Guide Complet",
      intro: "L'Ultimate Texas Hold'em est l'un des jeux de poker de casino les plus populaires au monde. Contrairement au poker traditionnel où vous jouez contre d'autres joueurs, ici vous affrontez le croupier. Notre version gratuite en ligne vous permet de pratiquer et maîtriser le jeu sans risquer d'argent réel.",
      
      rulesTitle: "📜 Règles Officielles",
      rules: [
        { title: "Mises Initiales", text: "Placez des mises Ante et Blind égales avant de recevoir vos cartes. La mise Trips optionnelle paie selon la force de votre main, indépendamment de celle du croupier." },
        { title: "Cartes Fermées", text: "Vous recevez 2 cartes privées (cartes fermées). Le croupier reçoit également 2 cartes face cachée." },
        { title: "Décision Pré-Flop", text: "Après avoir vu vos cartes, vous pouvez : Miser 3x ou 4x votre Ante (mains fortes), ou Parole (attendre plus de cartes)." },
        { title: "Le Flop", text: "3 cartes communes sont révélées. Si vous n'avez pas encore misé, vous pouvez : Miser 2x votre Ante, ou Parole à nouveau." },
        { title: "Turn & River", text: "Les 2 dernières cartes communes sont révélées. Si vous n'avez pas misé, vous devez : Miser 1x votre Ante, ou Vous coucher (perdre Ante et Blind)." },
        { title: "Abattage", text: "Les deux mains sont révélées. La meilleure main de 5 cartes gagne. Le croupier doit avoir une paire ou mieux pour 'qualifier'." },
      ],
      
      payoutsTitle: "💰 Tables de Paiement",
      tripsPayouts: [
        { hand: "Quinte Flush Royale", payout: "50:1" },
        { hand: "Quinte Flush", payout: "40:1" },
        { hand: "Carré", payout: "30:1" },
        { hand: "Full", payout: "8:1" },
        { hand: "Couleur", payout: "6:1" },
        { hand: "Suite", payout: "5:1" },
        { hand: "Brelan", payout: "3:1" },
      ],
      blindPayouts: [
        { hand: "Quinte Flush Royale", payout: "500:1" },
        { hand: "Quinte Flush", payout: "50:1" },
        { hand: "Carré", payout: "10:1" },
        { hand: "Full", payout: "3:1" },
        { hand: "Couleur", payout: "3:2" },
        { hand: "Suite", payout: "1:1" },
      ],
      
      strategyTitle: "🎯 Stratégie Gagnante",
      strategies: [
        { title: "Miser 4x Pré-Flop", situations: ["Toute paire (surtout les grosses paires)", "As assorti avec n'importe quelle carte", "As-Roi, As-Dame, As-Valet (assorti ou non)"] },
        { title: "Miser 3x Pré-Flop", situations: ["As avec 2-10 (non assorti)", "Roi-Dame assortis", "Deux cartes Valet ou plus"] },
        { title: "Parole Pré-Flop", situations: ["Mains faibles sans As ni paire", "Petites cartes connectées assorties", "Attendre le flop pour s'améliorer"] },
        { title: "Miser 2x au Flop", situations: ["Deux paires ou mieux", "Paire cachée (utilisant vos deux cartes)", "Quatre cartes pour une couleur ou suite ouverte"] },
        { title: "Miser 1x au River", situations: ["Toute main faite (paire ou mieux)", "Mieux vaut miser 1x que se coucher avec des mains marginales"] },
        { title: "Quand se Coucher", situations: ["Moins de 21 outs", "Pas de paire, pas de tirage après le river", "Mains très faibles sans amélioration possible"] },
      ],
      
      faqTitle: "❓ Questions Fréquentes",
      faqs: [
        { 
          q: "Qu'est-ce que l'Ultimate Texas Hold'em ?", 
          a: "L'Ultimate Texas Hold'em est un jeu de table de casino basé sur le Texas Hold'em poker. Vous jouez contre le croupier, pas contre d'autres joueurs. L'objectif est de faire la meilleure main de 5 cartes en utilisant vos 2 cartes et 5 cartes communes."
        },
        { 
          q: "Que se passe-t-il si le croupier ne qualifie pas ?", 
          a: "Si le croupier n'a pas au moins une paire, votre mise Ante est remboursée (égalité), mais vos mises Play et Blind sont toujours payées si vous gagnez."
        },
        { 
          q: "Quel est l'avantage de la maison ?", 
          a: "Avec une stratégie optimale, l'avantage de la maison est d'environ 2,19% sur la mise Ante. La mise Trips a un avantage plus élevé d'environ 3,5%."
        },
        { 
          q: "Puis-je jouer gratuitement ?", 
          a: "Oui ! Notre jeu utilise uniquement des jetons virtuels. C'est 100% gratuit sans argent réel - parfait pour apprendre et s'entraîner."
        },
        { 
          q: "Quelle est la différence entre Ante et Blind ?", 
          a: "Les deux sont des mises obligatoires égales. L'Ante paie 1:1 quand vous gagnez (ou égalité si le croupier ne qualifie pas). Le Blind a des bonus pour les mains fortes (Suite ou mieux)."
        },
        { 
          q: "Dois-je toujours jouer la mise Trips ?", 
          a: "La mise Trips est optionnelle et indépendante du jeu principal. Elle paie selon la force de votre main, que vous battiez le croupier ou non. Elle a un avantage maison plus élevé mais offre de gros gains pour les mains premium."
        },
      ],
      
      tipsTitle: "💡 Conseils de Pro",
      tips: [
        "Misez toujours 4x avec une paire - les probabilités favorisent le jeu agressif",
        "Ne vous couchez jamais avant le river sauf si vous n'avez absolument rien",
        "Le Blind est là où on gagne gros - il paie jusqu'à 500:1 pour une Quinte Flush Royale",
        "Utilisez notre fonction Conseils pour apprendre la stratégie optimale en jouant",
        "Gérez votre bankroll - les mises doivent être cohérentes par rapport à votre stack",
        "Entraînez-vous ici gratuitement avant de jouer dans de vrais casinos",
      ],
      
      historyTitle: "📚 Histoire du Jeu",
      history: "L'Ultimate Texas Hold'em a été créé par Roger Snow et introduit pour la première fois en 2006. Il est rapidement devenu l'un des jeux de table de casino les plus populaires grâce à sa profondeur stratégique et son gameplay excitant. Le jeu combine les mécaniques familières du Texas Hold'em poker avec les paris de style casino contre la maison. Aujourd'hui, il est proposé dans les casinos du monde entier et est devenu un incontournable des plateformes de jeux en ligne.",
      
      glossaryTitle: "📖 Glossaire",
      glossary: [
        { term: "Ante", def: "Mise initiale obligatoire placée avant de recevoir les cartes" },
        { term: "Blind", def: "Mise obligatoire égale placée avec l'Ante, avec des bonus" },
        { term: "Trips", def: "Mise optionnelle qui paie selon la force de votre main" },
        { term: "Play", def: "Mise additionnelle faite pendant la main (1x à 4x l'Ante)" },
        { term: "Qualifier", def: "Le croupier doit avoir une paire ou mieux" },
        { term: "Égalité", def: "Match nul - votre mise est remboursée" },
        { term: "Cartes Fermées", def: "Vos 2 cartes privées" },
        { term: "Cartes Communes", def: "5 cartes partagées (Flop, Turn, River)" },
      ],
    },
    
    de: {
      mainTitle: "Ultimate Texas Hold'em - Kompletter Leitfaden",
      intro: "Ultimate Texas Hold'em ist eines der beliebtesten Casino-Pokerspiele der Welt. Im Gegensatz zum traditionellen Poker, wo Sie gegen andere Spieler spielen, treten Sie hier gegen den Dealer an. Unsere kostenlose Online-Version ermöglicht es Ihnen, das Spiel zu üben und zu meistern, ohne echtes Geld zu riskieren.",
      
      rulesTitle: "📜 Offizielle Regeln",
      rules: [
        { title: "Anfangseinsätze", text: "Platzieren Sie gleiche Ante- und Blind-Einsätze, bevor Sie Karten erhalten. Der optionale Trips-Einsatz zahlt basierend auf Ihrer Handstärke, unabhängig von der Hand des Dealers." },
        { title: "Hole Cards", text: "Sie erhalten 2 private Karten (Hole Cards). Der Dealer erhält ebenfalls 2 verdeckte Karten." },
        { title: "Pre-Flop Entscheidung", text: "Nach dem Anschauen Ihrer Karten können Sie: 3x oder 4x Ihren Ante setzen (starke Hände), oder Checken (auf mehr Karten warten)." },
        { title: "Der Flop", text: "3 Gemeinschaftskarten werden aufgedeckt. Wenn Sie noch nicht gesetzt haben, können Sie: 2x Ihren Ante setzen, oder erneut Checken." },
        { title: "Turn & River", text: "Die letzten 2 Gemeinschaftskarten werden aufgedeckt. Wenn Sie noch nicht gesetzt haben, müssen Sie: 1x Ihren Ante setzen, oder Passen (Ante und Blind verlieren)." },
        { title: "Showdown", text: "Beide Hände werden aufgedeckt. Die beste 5-Karten-Pokerhand gewinnt. Der Dealer muss ein Paar oder besser haben, um zu 'qualifizieren'." },
      ],
      
      payoutsTitle: "💰 Auszahlungstabellen",
      tripsPayouts: [
        { hand: "Royal Flush", payout: "50:1" },
        { hand: "Straight Flush", payout: "40:1" },
        { hand: "Vierling", payout: "30:1" },
        { hand: "Full House", payout: "8:1" },
        { hand: "Flush", payout: "6:1" },
        { hand: "Straße", payout: "5:1" },
        { hand: "Drilling", payout: "3:1" },
      ],
      blindPayouts: [
        { hand: "Royal Flush", payout: "500:1" },
        { hand: "Straight Flush", payout: "50:1" },
        { hand: "Vierling", payout: "10:1" },
        { hand: "Full House", payout: "3:1" },
        { hand: "Flush", payout: "3:2" },
        { hand: "Straße", payout: "1:1" },
      ],
      
      strategyTitle: "🎯 Gewinnstrategie",
      strategies: [
        { title: "4x Pre-Flop setzen", situations: ["Jedes Paar (besonders hohe Paare)", "Suited Ass mit beliebiger Karte", "Ass-König, Ass-Dame, Ass-Bube (suited oder nicht)"] },
        { title: "3x Pre-Flop setzen", situations: ["Ass mit 2-10 (unsuited)", "König-Dame suited", "Zwei Karten Bube oder höher"] },
        { title: "Pre-Flop Checken", situations: ["Schwache Hände ohne Ass oder Paar", "Kleine suited Connectors", "Auf den Flop warten zur Verbesserung"] },
        { title: "2x am Flop setzen", situations: ["Zwei Paare oder besser", "Verstecktes Paar (beide Hole Cards nutzend)", "Vier zum Flush oder offene Straße"] },
        { title: "1x am River setzen", situations: ["Jede gemachte Hand (Paar oder besser)", "Besser 1x setzen als mit marginalen Händen passen"] },
        { title: "Wann Passen", situations: ["Weniger als 21 Outs", "Kein Paar, kein Draw nach dem River", "Sehr schwache Hände ohne Verbesserungsmöglichkeit"] },
      ],
      
      faqTitle: "❓ Häufig Gestellte Fragen",
      faqs: [
        { 
          q: "Was ist Ultimate Texas Hold'em?", 
          a: "Ultimate Texas Hold'em ist ein Casino-Tischspiel basierend auf Texas Hold'em Poker. Sie spielen gegen den Dealer, nicht gegen andere Spieler. Das Ziel ist es, die beste 5-Karten-Hand mit Ihren 2 Hole Cards und 5 Gemeinschaftskarten zu bilden."
        },
        { 
          q: "Was passiert, wenn der Dealer nicht qualifiziert?", 
          a: "Wenn der Dealer nicht mindestens ein Paar hat, wird Ihr Ante-Einsatz zurückgegeben (Push), aber Ihre Play- und Blind-Einsätze werden trotzdem bezahlt, wenn Sie gewinnen."
        },
        { 
          q: "Wie hoch ist der Hausvorteil?", 
          a: "Mit optimaler Strategie beträgt der Hausvorteil etwa 2,19% auf den Ante-Einsatz. Der Trips-Einsatz hat einen höheren Hausvorteil von etwa 3,5%."
        },
        { 
          q: "Kann ich kostenlos spielen?", 
          a: "Ja! Unser Spiel verwendet nur virtuelle Chips. Es ist 100% kostenlos ohne echtes Geld - perfekt zum Lernen und Üben."
        },
        { 
          q: "Was ist der Unterschied zwischen Ante und Blind?", 
          a: "Beide sind obligatorische gleiche Einsätze. Der Ante zahlt 1:1 bei Gewinn (oder Push, wenn der Dealer nicht qualifiziert). Der Blind hat Bonusauszahlungen für starke Hände (Straße oder besser)."
        },
        { 
          q: "Sollte ich immer den Trips-Einsatz spielen?", 
          a: "Der Trips-Einsatz ist optional und unabhängig vom Hauptspiel. Er zahlt basierend auf Ihrer Handstärke, unabhängig davon, ob Sie den Dealer schlagen. Er hat einen höheren Hausvorteil, bietet aber große Auszahlungen für Premium-Hände."
        },
      ],
      
      tipsTitle: "💡 Profi-Tipps",
      tips: [
        "Setzen Sie immer 4x mit einem Paar - die Quoten begünstigen aggressives Spiel",
        "Passen Sie nie vor dem River, es sei denn, Sie haben absolut nichts",
        "Der Blind ist, wo großes Geld gemacht wird - er zahlt bis zu 500:1 für Royal Flush",
        "Nutzen Sie unsere Tipps-Funktion, um optimale Strategie beim Spielen zu lernen",
        "Verwalten Sie Ihre Bankroll - Einsatzgrößen sollten konsistent zu Ihrem Stack sein",
        "Üben Sie hier kostenlos, bevor Sie in echten Casinos spielen",
      ],
      
      historyTitle: "📚 Geschichte des Spiels",
      history: "Ultimate Texas Hold'em wurde von Roger Snow entwickelt und 2006 erstmals eingeführt. Es wurde schnell zu einem der beliebtesten Casino-Tischspiele aufgrund seiner strategischen Tiefe und des aufregenden Gameplays. Das Spiel kombiniert die vertrauten Mechaniken des Texas Hold'em Poker mit Casino-Style Wetten gegen das Haus. Heute wird es in Casinos weltweit angeboten und ist ein fester Bestandteil von Online-Gaming-Plattformen.",
      
      glossaryTitle: "📖 Glossar",
      glossary: [
        { term: "Ante", def: "Obligatorischer Anfangseinsatz vor dem Kartenerhalt" },
        { term: "Blind", def: "Gleicher obligatorischer Einsatz neben Ante, mit Bonusauszahlungen" },
        { term: "Trips", def: "Optionaler Nebeneinsatz basierend auf Handstärke" },
        { term: "Play", def: "Zusätzlicher Einsatz während der Hand (1x bis 4x Ante)" },
        { term: "Qualifizieren", def: "Dealer muss Paar oder besser haben" },
        { term: "Push", def: "Unentschieden - Einsatz wird zurückgegeben" },
        { term: "Hole Cards", def: "Ihre 2 privaten Karten" },
        { term: "Gemeinschaftskarten", def: "5 geteilte Karten (Flop, Turn, River)" },
      ],
    },
  };

  const c = content[language] || content.en;

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const SectionHeader = ({ id, title, icon }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between p-4 rounded-xl transition-all hover:scale-[1.01]"
      style={{
        background: expandedSection === id 
          ? 'linear-gradient(145deg, rgba(255,215,0,0.15), rgba(255,140,0,0.1))'
          : 'linear-gradient(145deg, rgba(26,26,46,0.8), rgba(22,33,62,0.8))',
        border: `1px solid ${expandedSection === id ? '#ffd700' : 'rgba(255,215,0,0.2)'}`,
      }}
    >
      <h2 className="text-lg md:text-xl font-bold" style={{ color: '#ffd700' }}>
        {title}
      </h2>
      <span className="text-2xl transition-transform" style={{ 
        color: '#ffd700',
        transform: expandedSection === id ? 'rotate(180deg)' : 'rotate(0deg)'
      }}>
        ▼
      </span>
    </button>
  );

  return (
    <div className="mt-8 md:mt-12 px-4 md:px-8 pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Main Title */}
        <header className="text-center mb-8">
          <h1 
            className="text-2xl md:text-4xl font-black mb-4"
            style={{ 
              background: 'linear-gradient(180deg, #ffd700 0%, #ff8c00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {c.mainTitle}
          </h1>
          <p className="text-sm md:text-base max-w-2xl mx-auto" style={{ color: '#aaa' }}>
            {c.intro}
          </p>
        </header>

        {/* Accordion Sections */}
        <div className="space-y-3">
          
          {/* Rules Section */}
          <section>
            <SectionHeader id="rules" title={c.rulesTitle} />
            {expandedSection === 'rules' && (
              <div className="mt-2 p-4 md:p-6 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <div className="space-y-4">
                  {c.rules.map((rule, idx) => (
                    <article key={idx} className="flex gap-3">
                      <div 
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold"
                        style={{ background: 'linear-gradient(145deg, #ffd700, #b8860b)', color: '#1a1a2e' }}
                      >
                        {idx + 1}
                      </div>
                      <div>
                        <h3 className="font-bold mb-1" style={{ color: '#ffd700' }}>{rule.title}</h3>
                        <p className="text-sm" style={{ color: '#ccc' }}>{rule.text}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Payouts Section */}
          <section>
            <SectionHeader id="payouts" title={c.payoutsTitle} />
            {expandedSection === 'payouts' && (
              <div className="mt-2 p-4 md:p-6 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#a855f7' }}>
                      💎 TRIPS BONUS
                    </h3>
                    <div className="space-y-2">
                      {c.tripsPayouts.map((p, idx) => (
                        <div key={idx} className="flex justify-between p-2 rounded" style={{ background: 'rgba(168,85,247,0.1)' }}>
                          <span style={{ color: '#ccc' }}>{p.hand}</span>
                          <span className="font-bold" style={{ color: '#a855f7' }}>{p.payout}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#3b82f6' }}>
                      🎯 BLIND BONUS
                    </h3>
                    <div className="space-y-2">
                      {c.blindPayouts.map((p, idx) => (
                        <div key={idx} className="flex justify-between p-2 rounded" style={{ background: 'rgba(59,130,246,0.1)' }}>
                          <span style={{ color: '#ccc' }}>{p.hand}</span>
                          <span className="font-bold" style={{ color: '#3b82f6' }}>{p.payout}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Strategy Section */}
          <section>
            <SectionHeader id="strategy" title={c.strategyTitle} />
            {expandedSection === 'strategy' && (
              <div className="mt-2 p-4 md:p-6 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <div className="grid md:grid-cols-2 gap-4">
                  {c.strategies.map((strat, idx) => (
                    <article key={idx} className="p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}>
                      <h3 className="font-bold mb-2" style={{ color: '#22c55e' }}>{strat.title}</h3>
                      <ul className="space-y-1">
                        {strat.situations.map((sit, sidx) => (
                          <li key={sidx} className="text-sm flex items-start gap-2" style={{ color: '#ccc' }}>
                            <span style={{ color: '#22c55e' }}>•</span>
                            {sit}
                          </li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* FAQ Section */}
          <section>
            <SectionHeader id="faq" title={c.faqTitle} />
            {expandedSection === 'faq' && (
              <div className="mt-2 p-4 md:p-6 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <div className="space-y-4">
                  {c.faqs.map((faq, idx) => (
                    <article key={idx} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <h3 className="font-bold mb-2" style={{ color: '#ffd700' }}>
                        Q: {faq.q}
                      </h3>
                      <p className="text-sm" style={{ color: '#ccc' }}>
                        <strong style={{ color: '#22c55e' }}>A:</strong> {faq.a}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Tips Section */}
          <section>
            <SectionHeader id="tips" title={c.tipsTitle} />
            {expandedSection === 'tips' && (
              <div className="mt-2 p-4 md:p-6 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <ul className="space-y-3">
                  {c.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-2 rounded" style={{ background: 'rgba(255,215,0,0.05)' }}>
                      <span className="text-xl">💡</span>
                      <span style={{ color: '#ccc' }}>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* History Section */}
          <section>
            <SectionHeader id="history" title={c.historyTitle} />
            {expandedSection === 'history' && (
              <div className="mt-2 p-4 md:p-6 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <p style={{ color: '#ccc', lineHeight: 1.7 }}>{c.history}</p>
              </div>
            )}
          </section>

          {/* Glossary Section */}
          <section>
            <SectionHeader id="glossary" title={c.glossaryTitle} />
            {expandedSection === 'glossary' && (
              <div className="mt-2 p-4 md:p-6 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <div className="grid md:grid-cols-2 gap-3">
                  {c.glossary.map((item, idx) => (
                    <div key={idx} className="p-2 rounded" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <span className="font-bold" style={{ color: '#ffd700' }}>{item.term}:</span>
                      <span className="ml-2 text-sm" style={{ color: '#ccc' }}>{item.def}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

        </div>

        {/* Footer SEO Text */}
        <footer className="mt-8 pt-6 text-center" style={{ borderTop: '1px solid rgba(255,215,0,0.1)' }}>
          <p className="text-xs" style={{ color: '#666' }}>
            © 2024 Ultimate Texas Hold'em Online - Free Casino Poker Game | 
            Play responsibly | 18+ | No real money gambling
          </p>
        </footer>
      </div>
    </div>
  );
};

export default SEOContent;
